import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Clock, Smartphone, X, Loader2, DoorOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AddToWallet } from './AddToWallet';
import { ShareKeySheet } from './ShareKeySheet';
import { useDeviceStore } from '@/stores/deviceStore';
import { formatRemaining } from '@/lib/time';
import type { AccessKey, Device } from '@/types';

interface KeyCardProps {
  accessKey: AccessKey;
  device?: Device;
  canShareKeys?: boolean;
  onRevoke?: (keyId: string) => void;
  isRevoking?: boolean;
}

export function KeyCard({ accessKey, device, canShareKeys, onRevoke, isRevoking }: KeyCardProps) {
  const { activeDevice } = useDeviceStore();
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const expiresAt = new Date(accessKey.expires_at);
  const now = new Date();
  const remainingMs = expiresAt.getTime() - now.getTime();
  const isExpired = remainingMs <= 0;

  const handleRevoke = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmRevoke) {
      onRevoke?.(accessKey.id);
      setConfirmRevoke(false);
    } else {
      setConfirmRevoke(true);
    }
  };

  return (
    <div
      className={`relative w-[calc(100vw-2rem)] max-w-sm rounded-3xl overflow-hidden animate-key-in group transition-all
        ${isExpired ? 'opacity-60' : isRevoking ? 'opacity-50 scale-95' : ''}`}
    >
      {/* Glass surface */}
      <div className={`relative ${isExpired ? 'glass-thin' : 'glass-regular'} rounded-3xl`}>
        {/* One-shot specular sweep on entrance — pure white in dark, barely visible in light */}
        {!isExpired && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl">
            <div
              className="absolute top-0 h-full w-1/3 animate-glass-sweep"
              style={{
                background:
                  'linear-gradient(90deg, transparent 0%, hsl(var(--glass-shine)) 50%, transparent 100%)',
              }}
            />
          </div>
        )}

        <div className="relative p-0">
          {/* Device header */}
          <div className="px-5 pt-5 pb-3 border-b border-[hsl(var(--glass-stroke))]">
            <div className="flex flex-col items-center text-center gap-1.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--foreground)/0.08)] ring-1 ring-[hsl(var(--glass-stroke))]">
                <DoorOpen className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <p className="font-semibold text-base leading-tight tracking-tight">
                  {device?.name || activeDevice?.name || 'Enhet'}
                </p>
                {(device?.location || activeDevice?.location) && (
                  <p className="text-xs text-muted-foreground">
                    {device?.location || activeDevice?.location}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-2">
              {accessKey.access_method !== 'nfc' && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-2 py-0.5 rounded-md font-medium bg-[hsl(var(--foreground)/0.06)] border border-[hsl(var(--glass-stroke))] text-foreground"
                >
                  QR
                </Badge>
              )}
              {accessKey.access_method !== 'qr' && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-2 py-0.5 rounded-md font-medium bg-[hsl(var(--foreground)/0.06)] border border-[hsl(var(--glass-stroke))] text-foreground"
                >
                  NFC
                </Badge>
              )}
              {onRevoke && !isExpired && (
                <button
                  onClick={handleRevoke}
                  onBlur={() => setConfirmRevoke(false)}
                  className={`p-1 rounded-md transition-all ${
                    confirmRevoke
                      ? 'bg-destructive/20 text-destructive ring-1 ring-destructive/40'
                      : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--foreground)/0.08)]'
                  }`}
                  aria-label="Ta bort nyckel"
                >
                  {isRevoking ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>
            {confirmRevoke && (
              <p className="text-[10px] text-destructive text-center mt-1 animate-fade-in">
                Tryck igen för att ta bort
              </p>
            )}
          </div>

          {/* QR code */}
          <div className="px-5 py-5">
            {isExpired ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-sm font-medium text-destructive">Nyckeln har utgått</p>
                <p className="text-xs text-muted-foreground mt-1">Generera en ny nyckel</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                {/* QR is always white interior for scan reliability;
                    frame uses a neutral shadow that works in both modes */}
                <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-[hsl(var(--glass-shine))] via-[hsl(var(--glass-stroke))] to-transparent shadow-[0_8px_32px_-8px_hsl(var(--glass-shadow))]">
                  <div className="bg-white p-3 rounded-2xl">
                    <QRCodeSVG
                      value={accessKey.qr_string}
                      size={200}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                </div>

                {accessKey.access_method !== 'qr' && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Smartphone className="h-3.5 w-3.5" />
                    <span>Eller håll telefonen mot NFC-läsaren</span>
                  </div>
                )}
              </div>
            )}

            {/* Timer */}
            <div
              className={`flex items-center justify-center gap-2 text-sm mt-4 ${
                isExpired ? 'text-destructive' : 'text-muted-foreground'
              }`}
            >
              <Clock className="h-4 w-4" />
              {isExpired ? (
                <span className="font-medium">Utgången</span>
              ) : (
                <span className="font-medium">{formatRemaining(remainingMs)}</span>
              )}
              <span className="text-xs text-muted-foreground/60">· Port {accessKey.port_code}</span>
            </div>
          </div>

          {/* Wallet + Share */}
          {!isExpired && (
            <div className="px-5 pb-5 pt-2 border-t border-[hsl(var(--glass-stroke))]">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-xs text-muted-foreground font-medium">Lägg till i wallet</p>
                {canShareKeys && <ShareKeySheet keyId={accessKey.id} disabled={isExpired} />}
              </div>
              <AddToWallet keyId={accessKey.id} disabled={isExpired} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
